import React, { useState, useEffect, useCallback } from 'react';
import { subscribeRoom, leaveRoom } from '../shared/roomTx';
import { startChainGame, updatePlayerOrder } from '../shared/chainRoomTx';
import { FL_ROOM_STATUS, FIVE_LEAGUE, FL_GAME_MODE, CHAIN_SETTINGS } from '../shared/fiveLeague';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import './ChainOrder.css';

function ChainOrder({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [localOrder, setLocalOrder] = useState([]);

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      if (!data) {
        setError('ルームが見つかりません');
        return;
      }

      // このプレイヤーがルームに参加しているか確認
      const isPlayer = data.players?.some(p => p.playerId === playerId);
      if (!isPlayer) {
        setError('このルームに参加していません');
        return;
      }

      // チェーンモードでない場合はWait画面へ
      if (data.gameMode !== FL_GAME_MODE.CHAIN) {
        navigate('wait', { roomId });
        return;
      }

      setRoom(data);

      // 初回のみローカル順序を設定
      if (localOrder.length === 0 && data.playerOrder?.length > 0) {
        setLocalOrder([...data.playerOrder]);
      }

      // ゲーム開始したらChainGame画面へ
      if (data.status === FL_ROOM_STATUS.PLAYING) {
        navigate('chain-game', { roomId });
      }
    });

    return () => unsub();
  }, [roomId, playerId, navigate, localOrder.length]);

  // ドラッグ&ドロップハンドラ
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOrder = [...localOrder];
    const [dragged] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, dragged);
    setLocalOrder(newOrder);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // タッチ対応のスワップ
  const handleSwap = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newOrder = [...localOrder];
    [newOrder[fromIndex], newOrder[toIndex]] = [newOrder[toIndex], newOrder[fromIndex]];
    setLocalOrder(newOrder);
  };

  // シャッフル
  const handleShuffle = () => {
    const newOrder = [...localOrder];
    for (let i = newOrder.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newOrder[i], newOrder[j]] = [newOrder[j], newOrder[i]];
    }
    setLocalOrder(newOrder);
  };

  // ゲーム開始
  const handleStartGame = async () => {
    if (!room || starting) return;

    setStarting(true);
    setError('');

    // カスタム順序をインデックス配列に変換
    const orderIndices = localOrder.map(playerId =>
      room.playerOrder.indexOf(playerId)
    );

    const result = await startChainGame(roomId, playerId, orderIndices);
    if (!result.ok) {
      if (result.reason === 'not-enough-players') {
        setError(`最低${FIVE_LEAGUE.MIN_PLAYERS}人必要です`);
      } else if (result.reason === 'not-all-ready') {
        setError('全員が準備完了していません');
      } else {
        setError('ゲームを開始できませんでした');
      }
      setStarting(false);
    }
  };

  const handleLeave = async () => {
    await leaveRoom(roomId, playerId);
    navigate('home');
  };

  const handleBack = () => {
    navigate('wait', { roomId });
  };

  if (loading) {
    return (
      <div className="chain-order-container">
        <CyberBackground showParticles={true} />
        <div className="loading-spinner">読み込み中...</div>
      </div>
    );
  }

  if (error && !room) {
    return (
      <div className="chain-order-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">{error}</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  if (!room) return null;

  const isHost = room.hostId === playerId;
  const controllers = room.players?.filter(p => p.role !== 'display') || [];
  const answerCount = room.config?.answerCount || CHAIN_SETTINGS.ANSWER_COUNT_5;
  const timeLimit = answerCount === 10 ? CHAIN_SETTINGS.TIME_LIMIT_10 : CHAIN_SETTINGS.TIME_LIMIT_5;

  // localOrderからプレイヤー情報を取得
  const orderedPlayers = localOrder
    .map(pid => controllers.find(p => p.playerId === pid))
    .filter(Boolean);

  // 折り返しパターンをプレビュー
  const turnPattern = generateTurnPattern(orderedPlayers.length, Math.min(answerCount, 6));

  return (
    <div className="chain-order-container animate-fade-in">
      <CyberBackground showParticles={true} />

      <div className="chain-order-content">
        <button className="back-btn" onClick={handleBack}>
          ← 戻る
        </button>

        <div className="chain-order-header">
          <h1 className="chain-order-title">
            <NeonText color="pink" size="lg">⚡ アンサーチェーン</NeonText>
          </h1>
          <p className="chain-order-subtitle">
            <NeonText color="blue" size="sm">
              {answerCount}問 / {timeLimit}秒
            </NeonText>
          </p>
        </div>

        <GlowingBorder color="purple" className="order-info-box">
          <p className="order-info-text">
            回答順を決めてください
          </p>
          <p className="order-info-hint">
            {isHost ? 'ドラッグで順番を変更できます' : 'ホストが順番を決めています...'}
          </p>
        </GlowingBorder>

        {/* プレイヤー順序 */}
        <div className="player-order-list">
          {orderedPlayers.map((player, index) => (
            <GlowingBorder
              key={player.playerId}
              color={index === 0 ? 'accent' : 'blue'}
              className={`player-order-card ${draggedIndex === index ? 'dragging' : ''} ${isHost ? 'draggable' : ''}`}
              draggable={isHost}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className="player-order-number">{index + 1}</div>
              <div className="player-order-info">
                <span className="player-order-name">
                  {player.nickname}
                  {player.playerId === room.hostId && <span className="host-badge">👑</span>}
                </span>
              </div>
              {isHost && (
                <div className="player-order-arrows">
                  <button
                    className="arrow-btn"
                    onClick={() => handleSwap(index, Math.max(0, index - 1))}
                    disabled={index === 0}
                  >
                    ▲
                  </button>
                  <button
                    className="arrow-btn"
                    onClick={() => handleSwap(index, Math.min(orderedPlayers.length - 1, index + 1))}
                    disabled={index === orderedPlayers.length - 1}
                  >
                    ▼
                  </button>
                </div>
              )}
            </GlowingBorder>
          ))}
        </div>

        {/* 折り返しプレビュー */}
        <div className="turn-preview">
          <span className="turn-preview-label">順番:</span>
          <div className="turn-preview-pattern">
            {turnPattern.map((slot, i) => (
              <span key={i} className="turn-slot">
                {orderedPlayers[slot]?.nickname?.charAt(0) || '?'}
                {i < turnPattern.length - 1 && <span className="turn-arrow">→</span>}
              </span>
            ))}
            {answerCount > 6 && <span className="turn-more">...</span>}
          </div>
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="chain-order-actions">
          {isHost && (
            <>
              <CyberButton
                variant="primary"
                className="shuffle-btn"
                onClick={handleShuffle}
              >
                🎲 シャッフル
              </CyberButton>
              <CyberButton
                variant="accent"
                className="start-btn"
                onClick={handleStartGame}
                disabled={starting || orderedPlayers.length < FIVE_LEAGUE.MIN_PLAYERS}
              >
                {starting ? '開始中...' : 'ゲームスタート！'}
              </CyberButton>
            </>
          )}

          <button className="cyber-btn-secondary leave-btn" onClick={handleLeave}>
            退出する
          </button>
        </div>
      </div>
    </div>
  );
}

// 折り返しパターン生成（プレビュー用）
function generateTurnPattern(playerCount, previewCount) {
  const pattern = [];
  let forward = true;
  let pos = 0;

  for (let i = 0; i < previewCount; i++) {
    pattern.push(pos);

    if (forward) {
      if (pos >= playerCount - 1) {
        forward = false;
      } else {
        pos++;
      }
    } else {
      if (pos <= 0) {
        forward = true;
      } else {
        pos--;
      }
    }
  }
  return pattern;
}

export default ChainOrder;
